/**
 * Application Configuration
 * 
 * Centralized configuration management for the Weederboard frontend.
 * This module handles environment variables and provides configuration
 * for different deployment environments.
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  // apiKey: process.env.REACT_APP_API_KEY || 'test-key-123', // REMOVED for testing
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000, // 30 seconds
};

/**
 * Rate Limiting Configuration
 */
export const RATE_LIMIT_CONFIG = {
  maxRequests: parseInt(process.env.REACT_APP_RATE_LIMIT_MAX_REQUESTS) || 100,
  windowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW_MS) || 60000,
  burstLimit: parseInt(process.env.REACT_APP_RATE_LIMIT_BURST) || 20,
  burstWindowMs: parseInt(process.env.REACT_APP_RATE_LIMIT_BURST_WINDOW_MS) || 10000,
};

/**
 * Debug Configuration
 */
export const DEBUG_CONFIG = {
  apiLogging: process.env.REACT_APP_DEBUG_API === 'true',
  rateLimiterLogging: process.env.REACT_APP_DEBUG_RATE_LIMITER === 'true',
  verbose: process.env.NODE_ENV === 'development',
};

/**
 * Feature Flags
 */
export const FEATURE_FLAGS = {
  analytics: process.env.REACT_APP_ENABLE_ANALYTICS !== 'false', // Default true
  errorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING !== 'false', // Default true
  beta: process.env.REACT_APP_ENABLE_BETA_FEATURES === 'true',
  landingPageActive: process.env.REACT_APP_LANDING_PAGE_ACTIVE === 'ACTIVE', // Default false for development
};

/**
 * App Configuration
 */
export const APP_CONFIG = {
  name: 'Weederboard',
  version: process.env.REACT_APP_VERSION || '1.0.0',
  environment: process.env.NODE_ENV || 'development',
  buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString(),
};

/**
 * Get the appropriate API URL based on HTTPS configuration
 */
export const getApiUrl = () => {
  return API_CONFIG.baseUrl;
};

/**
 * Validation functions
 */
export const validateConfig = () => {
  const errors = [];
  const warnings = [];

  // Check required configurations for production
  if (APP_CONFIG.environment === 'production') {
    // API key validation removed for testing
    // if (!API_CONFIG.apiKey) {
    //   errors.push('API_KEY is required for production deployment');
    // }
    
    const currentApiUrl = getApiUrl();
    if (currentApiUrl.includes('localhost')) {
      warnings.push('API_URL points to localhost in production');
    }

    // Warn if HTTPS is not enabled in production
    // if (!API_CONFIG.useHttps) { // This line is removed as HTTPS is no longer handled by API_CONFIG
    //   warnings.push('HTTPS is not enabled in production environment');
    // }
  }

  // Validate rate limiting configuration
  if (RATE_LIMIT_CONFIG.maxRequests <= 0) {
    errors.push('RATE_LIMIT_MAX_REQUESTS must be greater than 0');
  }

  if (RATE_LIMIT_CONFIG.burstLimit > RATE_LIMIT_CONFIG.maxRequests) {
    warnings.push('Burst limit is higher than max requests per window');
  }

  return { errors, warnings };
};

/**
 * Get configuration summary for debugging
 */
export const getConfigSummary = () => {
  return {
    app: {
      name: APP_CONFIG.name,
      version: APP_CONFIG.version,
      environment: APP_CONFIG.environment,
    },
    api: {
      baseUrl: API_CONFIG.baseUrl,
      // httpsUrl: API_CONFIG.httpsUrl, // Removed as HTTPS is no longer handled by API_CONFIG
      // useHttps: API_CONFIG.useHttps, // Removed as HTTPS is no longer handled by API_CONFIG
      currentUrl: getApiUrl(),
      hasApiKey: false, // API key disabled for testing
      timeout: API_CONFIG.timeout,
    },
    rateLimit: {
      maxRequests: RATE_LIMIT_CONFIG.maxRequests,
      windowMs: RATE_LIMIT_CONFIG.windowMs,
      burstLimit: RATE_LIMIT_CONFIG.burstLimit,
    },
    features: FEATURE_FLAGS,
    debug: DEBUG_CONFIG,
  };
};

/**
 * Initialize configuration and log any issues
 */
export const initializeConfig = () => {
  const { errors, warnings } = validateConfig();
  
  if (errors.length > 0) {
    console.error('Configuration errors:', errors);
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`);
  }
  
  if (warnings.length > 0) {
    console.warn('Configuration warnings:', warnings);
  }
  
  if (DEBUG_CONFIG.verbose) {
    console.log('Configuration loaded:', getConfigSummary());
  }
};

// Initialize configuration on module load
initializeConfig();

const config = {
  API_CONFIG,
  RATE_LIMIT_CONFIG,
  DEBUG_CONFIG,
  FEATURE_FLAGS,
  APP_CONFIG,
  getApiUrl,
  validateConfig,
  getConfigSummary,
  initializeConfig,
};

export default config;
